import {Injectable} from '@angular/core';
import {ApplicationInsights} from '@microsoft/applicationinsights-web';
import {environment} from '../../../environments/environment';
import * as uuid from 'uuid';
import {MsalService} from '@azure/msal-angular';
import {NgxFancyLoggerService} from 'ngx-fancy-logger';
import {AccountInfo} from '@azure/msal-browser';
import {XYChart} from '@amcharts/amcharts4/charts';

const MAX_LOG_MESSAGES = 50;

@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  appInsights: ApplicationInsights;
  user: string;

  logMessages = [];

  constructor(private msalService: MsalService, private logger: NgxFancyLoggerService) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        endpointUrl: `${environment.apiURL}/metrics/v2/track`,
        enableAutoRouteTracking: true,
      }
    });

    this.appInsights.loadAppInsights();

    if (environment.emitLogs) {
      this.printWelcomeMessage();
    }

    (window as any).printLogMessages = () => {
      console.log(this.logMessages);
    };

    window.addEventListener('unhandledrejection', event => {
      this.log('Unhandled rejection:');
      this.error(event); // do whatever you want with the error
    }, true);

    window.addEventListener('error', event => {
      this.log('Error:');
      this.error(event); // do whatever you want with the error
    }, true);

    window.addEventListener('rejectionhandled', (event) => {
      this.log('Rejection handled:');
      this.error(event);
    });

    window.onerror = (msg, url, line, col, error) => {
      logger.error(msg, url, line, col, error); // do whatever you want with the error
    };

    this.setupUser();
  }

  private static tryStringify(object): boolean {
    try {
      JSON.stringify(object);
      return true;
    } catch {
      return false;
    }
  }

  new<T>(scope: string): Logger<T> {
    return new Logger<T>(scope, this);
  }

  setupUser() {
    const account: AccountInfo = this.msalService.instance.getActiveAccount();

    if (!!account) {
      const userEmailAddress = account.username;

      if (!!!this.user || this.user !== userEmailAddress) {
        this.log('Loading application insights - user: ' + userEmailAddress);
        this.user = userEmailAddress;
        this.appInsights.setAuthenticatedUserContext(userEmailAddress);
      }
    }
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({name}, {...properties, user: this.user});
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric({name, average}, {...properties, user: this.user});
  }

  logException(exception: Error, severityLevel?: number) {
    const errorID = `${this.user}-${uuid.v4()}`;
    this.appInsights.trackEvent({name: 'Exception'}, {user: this.user});
    this.appInsights.trackException({id: errorID, exception, severityLevel});
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({message}, {...properties, user: this.user});
  }

  error(...args: any) {
    this.appendError(args);

    if (environment.emitLogs) {
      this.logger.error(...args);
    }
  }

  log(...args: any) {
    this.appendMessage(...args);

    if (environment.emitLogs) {
      this.logger.info(...args);
    }
  }

  logPrefix(prefix: string, ...args: any) {
    this.appendMessage(...args);

    if (environment.emitLogs) {
      this.logger.info(...[`[${prefix}]`, ...args]);
    }
  }

  logWarning(...args: any) {
    this.appendMessage(...args);

    if (environment.emitLogs) {
      this.logger.warning(...args);
    }
  }

  private printWelcomeMessage() {
    this.logger.header('Welcome to TORCH', {color: 'red', fontSize: 15});
  }


  private appendError(err: any) {
    if (err.length > 1) {
      const context = err[0];
      this.logMessages.push({message: context, ...err.slice(1)});
      return;
    }

    this.logMessages = this.logMessages.slice(0, MAX_LOG_MESSAGES);
  }

  private appendMessage(...args: any) {
    if (args.length > 1) {
      if (!!args[0] && typeof (args[0]) === 'string') {
        const message = args[0];
        const object = args.slice(1);

        if (!(object instanceof XYChart) && LoggingService.tryStringify(object)) {
          this.logMessages.push({
            message,
            ...object
          });

          this.logMessages = this.logMessages.slice(0, MAX_LOG_MESSAGES);
        }

        return;
      }
    }

    this.logMessages.push.apply(this.logMessages, args);
    this.logMessages = this.logMessages.slice(0, MAX_LOG_MESSAGES);
  }
}

export class Logger<T> {

  constructor(private scope: string, private service: LoggingService) {
  }

  log(...args: any) {
   this.service.logPrefix(this.scope, ...args);
  }

}
